<template>
  <div class="station-certification" v-loading="loading">
    <h1 class="title">Gas quality <icon-button icon="el-icon-setting" @click="showDialog = true" v-if="canManageCertifications" /></h1>

    <div v-if="!loading && !latest" class="empty">
      No analysis on file
      <span v-if="station.gasQualityStatus === 'NeverCertified'" class="hint">
        This station has never been certified.
      </span>
    </div>

    <div v-else-if="latest" class="summary">
      <div class="row">
        <span class="label">Status</span>
        <certification-status-badge
          :status="station.gasQualityStatus != null ? station.gasQualityStatus : 'NeverCertified'" />
      </div>
      <div class="row">
        <span class="label">Certified</span>
        <span class="value">{{ formatDate(latest.certifiedAt) }}</span>
      </div>
      <div class="row">
        <span class="label">Expires</span>
        <span class="value"><span class="countdown">{{ expiryCountdown }}</span> {{ formatDate(latest.expiresAt) }}</span>
      </div>
      <div v-if="latest.performedByName" class="row">
        <span class="label">Performed by</span>
        <span class="value">{{ latest.performedByName }}</span>
      </div>
      <div v-if="latest.documentFile" class="document-row">
        <a :href="latest.documentFile.path" target="_blank" rel="noopener" class="document-link">
          <i class="el-icon-document"></i>
          View certification document
        </a>
      </div>
    </div>

    <el-dialog
      v-if="showDialog"
      :visible.sync="showDialog"
      :append-to-body="true"
      append-to-body
      width="900px"
      top="3vh"
      :title="`Gas quality certifications · ${station.name}`">
      <certification-manager
        v-if="showDialog"
        :station="station"
        @refresh="onRefresh" />
    </el-dialog>
  </div>
</template>

<script>
import CertificationStatusBadge from './CertificationStatusBadge'
import CertificationManager from './CertificationManager'

export default {
  name: 'StationCertification',
  components: { CertificationStatusBadge, CertificationManager },
  props: {
    station: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      loading: true,
      certifications: [],
      showDialog: false
    }
  },
  computed: {
    canManageCertifications () {
      if (this.$auth.user.isSystem) return true
      if (!this.station.organization) return false
      const userOrgs = (this.$root && this.$root.userOrganizations) || []
      return userOrgs.some(uo =>
        uo.organization &&
        uo.organization.id === this.station.organization.id &&
        uo.isOrganizationAdmin
      )
    },
    latest () {
      if (!this.certifications.length) return null
      return this.certifications[0]
    },
    expiryCountdown () {
      if (!this.latest || !this.latest.expiresAt) return ''
      const ms = new Date(this.latest.expiresAt).getTime() - Date.now()
      const days = Math.floor(ms / (1000 * 60 * 60 * 24))
      if (days > 0) return `(in ${days} days)`
      if (days === 0) return '(today)'
      return `(${Math.abs(days)} days ago)`
    }
  },
  methods: {
    formatDate (value) {
      if (!value) return '—'
      const d = new Date(value)
      if (isNaN(d.getTime())) return value
      return d.toLocaleDateString()
    },
    async fetchCertifications () {
      this.loading = true
      try {
        const url = this.$api.station(this.$route.params.sId).certifications
        const response = await this.$http.get(url)
        this.certifications = Array.isArray(response.data) ? response.data : []
      } catch (e) {
        this.certifications = []
      } finally {
        this.loading = false
      }
    },
    onRefresh () {
      this.fetchCertifications()
      this.$emit('refresh')
    }
  },
  created () {
    this.fetchCertifications()
  }
}
</script>

<style lang="scss" scoped>
  .station-certification {
    min-height: 100px;
    margin: 20px;
  }

  h1 {
      display: flex;
      align-items: center;
      justify-content: space-between;
      color: #909399;
      text-transform: none;
      font-size: 14px;
      margin: 0px;
    }

  .empty {
    color: $text-light;
    font-size: 12px;
    text-align: center;
    padding: 20px 0;

    .hint {
      display: block;
      margin-top: 4px;
      font-size: 11px;
    }
  }

  .summary {
    .row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 6px 0;
      border-bottom: 1px solid $border;
      font-size: 13px;

      &:last-child {
        border-bottom: none;
      }

      .label {
        color: $text-light;
        font-size: 12px;
      }

      .value {
        text-align: right;
      }

      .countdown {
        margin-left: 6px;
        font-size: 11px;
        color: $text-light;
      }
    }
  }

  .actions {
    margin-top: 15px;
    display: flex;
    justify-content: flex-end;
  }

  .document-row {
    padding: 10px 0 4px 0;
  }

  .document-link {
    display: inline-flex;
    align-items: center;
    font-size: 13px;
    color: #008fcc;
    text-decoration: none;

    i {
      margin-right: 6px;
    }

    &:hover {
      text-decoration: underline;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/dashboard/stations/station/Certification/StationCertification.vue